/**
 * Node Registry (Canvas V2)
 *
 * Central registry for all canvas node types.
 * Maps type strings to React components (wrapped with error boundaries),
 * schema objects, and provides helper functions.
 *
 * Key V2 changes from V1:
 * - crypto.randomUUID() for node IDs (BUILD_ORDER Q2)
 * - JSON-only schemas (BUILD_ORDER Q4)
 * - No static nodeCategories map (BUILD_ORDER Q5)
 * - PlaceholderNode for types not yet ported to canvas-v2
 */

import { memo } from 'react'
import { Position, type NodeProps } from 'reactflow'
import { withNodeErrorBoundary } from './NodeErrorBoundary'
import { TypedHandle } from './TypedHandle'
import { createDebug } from '../../utils/debugLog'

const sizeLog = createDebug('dw:size:registry')

// Sprint 3: Designer node components
import { CharacterDesignerNode } from './nodes/CharacterDesignerNode'
import { LocationDesignerNode } from './nodes/LocationDesignerNode'
import { ObjectDesignerNode } from './nodes/ObjectDesignerNode'

// Sprint 4: Upload + Display node components
import { ImageUploadNode } from './nodes/ImageUploadNode'

// Sprint 6: Image Generation nodes
import { ImageGeneratorNode } from './nodes/ImageGeneratorNode'
import { VideoUploadNode } from './nodes/VideoUploadNode'
import { AudioUploadNode } from './nodes/AudioUploadNode'

// Sprint 7: Video Generation node
import { VideoGeneratorNode } from './nodes/VideoGeneratorNode'

// Sprint 11: Sound Expert node
import { SoundExpertNode } from './nodes/SoundExpertNode'

// Sprint 18b: Video Editor node
import { VideoEditorNode } from './nodes/VideoEditorNode'

// Sprint 20: Script Writer node
import { ScriptWriterNode } from './nodes/ScriptWriterNode'

// Group node
import { GroupNode } from './nodes/GroupNode'

// JSON schema imports (all schemas are JSON-only per BUILD_ORDER Q4)
import characterDesignerSchema from '../../../../../schemas/nodes/characterDesigner.json'
import locationDesignerSchema from '../../../../../schemas/nodes/locationDesigner.json'
import objectDesignerSchema from '../../../../../schemas/nodes/objectDesigner.json'
import imageGeneratorSchema from '../../../../../schemas/nodes/imageGenerator.json'
import videoGeneratorSchema from '../../../../../schemas/nodes/videoGenerator.json'
import scriptWriterSchema from '../../../../../schemas/nodes/scriptWriter.json'
import screenplayWriterSchema from '../../../../../schemas/nodes/screenplayWriter.json'
import storyboardDesignerSchema from '../../../../../schemas/nodes/storyboardDesigner.json'
import speechGeneratorSchema from '../../../../../schemas/nodes/speechGenerator.json'
import musicGeneratorSchema from '../../../../../schemas/nodes/musicGenerator.json'
import videoEditorSchema from '../../../../../schemas/nodes/videoEditor.json'
import generatedImageWithHistorySchema from '../../../../../schemas/nodes/generatedImageWithHistory.json'
import webtoonPanelGeneratorSchema from '../../../../../schemas/nodes/webtoonPanelGenerator.json'
import webtoonGroupSchema from '../../../../../schemas/nodes/webtoonGroup.json'
import shortVideoGroupSchema from '../../../../../schemas/nodes/shortVideoGroup.json'
import groupSchema from '../../../../../schemas/nodes/nodeGroup.json'

// New JSON schemas created for Input/Media categories
import imageSchema from '../../../../../schemas/nodes/image.json'
import videoSchema from '../../../../../schemas/nodes/video.json'
import audioSchema from '../../../../../schemas/nodes/audio.json'
import textSchema from '../../../../../schemas/nodes/text.json'
import promptInputSchema from '../../../../../schemas/nodes/promptInput.json'
import styleSelectorSchema from '../../../../../schemas/nodes/styleSelector.json'
import panelSourceSchema from '../../../../../schemas/nodes/panelSource.json'
import directorSchema from '../../../../../schemas/nodes/director.json'
import soundExpertSchema from '../../../../../schemas/nodes/soundExpert.json'

// =============================================================================
// Types
// =============================================================================

export interface PortDef {
  id: string
  type: string
  label: string
  description?: string
  required?: boolean
  default?: unknown
  options?: string[]
}

export interface NodeSchema {
  type: string
  label: string
  category: string
  icon?: string
  description?: string
  inputs: PortDef[]
  outputs: PortDef[]
  defaults: Record<string, unknown>
  backendNodeId?: string
  backendType?: string
  executable?: boolean
  layout?: Record<string, { width: number; height: number }>
  footerHeight?: number
  config?: Array<Record<string, unknown>>
}

// =============================================================================
// Schema Derivation
// =============================================================================

/**
 * Maps a port definition to its data field name.
 * The port ID IS the data field name — no translation needed.
 * @deprecated Use port.id directly. Kept for backward compatibility.
 */
export function portIdToDataField(input: { id: string }): string {
  return input.id
}

/**
 * Enrich a raw JSON schema with computed defaults.
 */
function enrichSchema(raw: Record<string, unknown>): NodeSchema {
  const inputs = (raw.inputs as PortDef[]) || []
  const defaults = Object.fromEntries(
    inputs
      .filter((i: PortDef) => 'default' in i)
      .map((i: PortDef) => [portIdToDataField(i), i.default])
  )
  return {
    ...raw,
    inputs,
    outputs: (raw.outputs as PortDef[]) || [],
    defaults,
  } as NodeSchema
}

// =============================================================================
// Schema Registry (JSON-only, no inline TypeScript schemas)
// =============================================================================

export const nodeSchemas: Record<string, NodeSchema> = {
  // Agentic nodes
  characterDesigner: enrichSchema(characterDesignerSchema),
  locationDesigner: enrichSchema(locationDesignerSchema),
  objectDesigner: enrichSchema(objectDesignerSchema),
  imageGenerator: enrichSchema(imageGeneratorSchema),
  videoGenerator: enrichSchema(videoGeneratorSchema),
  scriptWriter: enrichSchema(scriptWriterSchema),
  screenplayWriter: enrichSchema(screenplayWriterSchema),
  storyboardDesigner: enrichSchema(storyboardDesignerSchema),
  speechGenerator: enrichSchema(speechGeneratorSchema),
  musicGenerator: enrichSchema(musicGeneratorSchema),
  videoEditor: enrichSchema(videoEditorSchema),
  webtoonPanelGenerator: enrichSchema(webtoonPanelGeneratorSchema),
  soundExpert: enrichSchema(soundExpertSchema),
  director: enrichSchema(directorSchema),

  // Group nodes
  webtoonGroup: enrichSchema(webtoonGroupSchema),
  shortVideoGroup: enrichSchema(shortVideoGroupSchema),
  nodeGroup: enrichSchema(groupSchema),

  // Media/Asset nodes
  image: enrichSchema(imageSchema),
  video: enrichSchema(videoSchema),
  audio: enrichSchema(audioSchema),
  generatedImageWithHistory: enrichSchema(generatedImageWithHistorySchema),

  // Input nodes
  text: enrichSchema(textSchema),
  promptInput: enrichSchema(promptInputSchema),
  styleSelector: enrichSchema(styleSelectorSchema),
  panelSource: enrichSchema(panelSourceSchema),
}

// =============================================================================
// Placeholder Node (renders for types not yet ported to canvas-v2)
// =============================================================================

interface PlaceholderNodeData {
  status?: string
  [key: string]: unknown
}

const PlaceholderNode = memo(function PlaceholderNode({ id, data, type }: NodeProps<PlaceholderNodeData>) {
  const schema = type ? nodeSchemas[type] : undefined
  const label = schema?.label || type || 'Unknown'
  const icon = schema?.icon || '\u{1F4E6}'

  return (
    <div className="rounded-lg shadow bg-card border border-border min-w-[160px] max-w-[280px]">
      <div className="px-3 py-2 bg-muted/50 border-b border-border rounded-t-lg flex items-center gap-2">
        <span className="text-sm">{icon}</span>
        <span className="text-xs font-medium text-foreground truncate">{label}</span>
        <div
          className={`ml-auto w-2 h-2 rounded-full ${
            data?.status === 'running' ? 'bg-yellow-500 animate-pulse' :
            data?.status === 'error' ? 'bg-red-500' :
            data?.status === 'completed' ? 'bg-green-500' :
            'bg-gray-500'
          }`}
        />
      </div>

      {/* Input handles */}
      {schema?.inputs.map((port, i) => (
        <TypedHandle
          key={port.id}
          type="target"
          position={Position.Left}
          id={port.id}
          portType={port.type}
          style={{ top: `${((i + 1) / (schema.inputs.length + 1)) * 100}%` }}
        />
      ))}

      {/* Output handles */}
      {schema?.outputs.map((port, i) => (
        <TypedHandle
          key={port.id}
          type="source"
          position={Position.Right}
          id={port.id}
          portType={port.type}
          style={{ top: `${((i + 1) / (schema.outputs.length + 1)) * 100}%` }}
        />
      ))}

      <div className="px-3 py-2">
        <p className="text-xxs text-muted-foreground">
          {schema?.description || `${label} node`}
        </p>
      </div>
      {id && (
        <div className="px-3 pb-2">
          <p className="text-xxs text-foreground/20 font-mono truncate">{id}</p>
        </div>
      )}
    </div>
  )
})

// =============================================================================
// Unknown Node (fallback for types not in nodeSchemas)
// =============================================================================

const UnknownNode = memo(function UnknownNode({ type }: NodeProps) {
  return (
    <div className="rounded-lg shadow bg-card border border-destructive/50 min-w-[200px] max-w-[280px]">
      <div className="px-3 py-2 bg-destructive/10 border-b border-destructive/30 rounded-t-lg flex items-center gap-2">
        <span className="text-sm">⚠️</span>
        <span className="text-xs font-medium text-destructive">Unknown Node</span>
      </div>
      <div className="px-3 py-3">
        <p className="text-xs text-text-secondary">
          Node type <code className="text-xxs bg-surface-2 px-1 py-0.5 rounded">{type}</code> is not recognized.
        </p>
        <p className="text-xxs text-text-muted mt-1">
          Delete this node or update the app.
        </p>
      </div>
    </div>
  )
})

// =============================================================================
// Node Types Registry (component map for React Flow)
// =============================================================================

function buildNodeTypes(): Record<string, React.ComponentType<NodeProps>> {
  const types: Record<string, React.ComponentType<NodeProps>> = {}

  // Map of node types that have real V2 implementations
  const v2Components: Record<string, React.ComponentType<NodeProps>> = {
    characterDesigner: CharacterDesignerNode as unknown as React.ComponentType<NodeProps>,
    locationDesigner: LocationDesignerNode as unknown as React.ComponentType<NodeProps>,
    objectDesigner: ObjectDesignerNode as unknown as React.ComponentType<NodeProps>,
    image: ImageUploadNode as unknown as React.ComponentType<NodeProps>,
    imageGenerator: ImageGeneratorNode as unknown as React.ComponentType<NodeProps>,
    video: VideoUploadNode as unknown as React.ComponentType<NodeProps>,
    audio: AudioUploadNode as unknown as React.ComponentType<NodeProps>,
    generatedImageWithHistory: ImageGeneratorNode as unknown as React.ComponentType<NodeProps>,
    videoGenerator: VideoGeneratorNode as unknown as React.ComponentType<NodeProps>,
    soundExpert: SoundExpertNode as unknown as React.ComponentType<NodeProps>,
    videoEditor: VideoEditorNode as unknown as React.ComponentType<NodeProps>,
    scriptWriter: ScriptWriterNode as unknown as React.ComponentType<NodeProps>,
    nodeGroup: GroupNode as unknown as React.ComponentType<NodeProps>,
  }

  for (const [key, schema] of Object.entries(nodeSchemas)) {
    const component = v2Components[key] || PlaceholderNode
    types[key] = withNodeErrorBoundary(component as React.ComponentType<NodeProps>, schema.label)
  }
  // Fallback for node types not in nodeSchemas (e.g. removed or from a newer version)
  types.default = withNodeErrorBoundary(UnknownNode as React.ComponentType<NodeProps>, 'Unknown')
  return types
}

export const nodeTypes = buildNodeTypes()

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get schema for a specific node type.
 * Returns undefined for unknown types.
 */
export function getNodeSchema(type: string): NodeSchema | undefined {
  return nodeSchemas[type]
}

/**
 * Get all nodes grouped by their schema category field.
 * This is the authoritative grouping (BUILD_ORDER Q5).
 */
export function getNodesByCategory(): Record<string, NodeSchema[]> {
  return Object.values(nodeSchemas).reduce(
    (acc, schema) => {
      const cat = schema.category || 'General'
      if (!acc[cat]) {
        acc[cat] = []
      }
      acc[cat].push(schema)
      return acc
    },
    {} as Record<string, NodeSchema[]>
  )
}

/**
 * List all available node type strings.
 */
export function getAvailableNodeTypes(): string[] {
  return Object.keys(nodeSchemas)
}

/**
 * Create a new React Flow node object with schema defaults.
 * Uses crypto.randomUUID() for IDs (BUILD_ORDER Q2).
 *
 * @throws Error if the type has no schema
 */
export function createNode(
  type: string,
  position: { x: number; y: number },
  additionalData: Record<string, unknown> = {}
) {
  const schema = nodeSchemas[type]
  if (!schema) {
    throw new Error(`Unknown node type: ${type}`)
  }

  return {
    id: crypto.randomUUID(),
    type,
    position,
    data: {
      ...schema.defaults,
      status: 'idle',
      ...additionalData,
    },
  }
}

/**
 * Get the backend executor ID for a frontend node type.
 * Checks backendNodeId/backendType on schema, else converts camelCase to snake_case.
 */
export function getBackendNodeId(nodeType: string): string {
  const schema = nodeSchemas[nodeType]
  if (schema) {
    if (schema.backendNodeId) return schema.backendNodeId
    if (schema.backendType) return schema.backendType as string
  }
  // Fallback: convert camelCase to snake_case
  return nodeType.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
}

/**
 * Check if a node type is executable (has a backend executor).
 * Returns false for unknown types.
 */
export function isNodeExecutable(nodeType: string): boolean {
  const schema = nodeSchemas[nodeType]
  return schema?.executable === true
}

/**
 * Get the layout dimensions for a node type.
 * Looks up the aspect-ratio-specific size first, then falls back to '*' wildcard.
 * Returns a default fallback if the type has no schema or no layout.
 * Also returns footerHeight (per-type footer chrome height, 0 if absent).
 */
export function getNodeDimensions(
  type: string,
  aspectRatio?: string,
): { width: number; height: number; footerHeight: number } {
  const schema = nodeSchemas[type]
  const footerHeight = schema?.footerHeight ?? 0
  if (!schema?.layout) {
    sizeLog('[size] getNodeDimensions: type=%s ratio=%s -> 315x300 (no schema/layout)', type, aspectRatio)
    return { width: 315, height: 300, footerHeight }
  }

  let result: { width: number; height: number; footerHeight: number }
  if (aspectRatio && schema.layout[aspectRatio]) {
    result = { ...schema.layout[aspectRatio], footerHeight }
  } else if (schema.layout['*']) {
    result = { ...schema.layout['*'], footerHeight }
  } else {
    const entries = Object.values(schema.layout)
    if (entries.length > 0) {
      result = { ...entries[0], footerHeight }
    } else {
      result = { width: 315, height: 300, footerHeight }
    }
  }

  sizeLog('[size] getNodeDimensions: type=%s ratio=%s -> %dx%d', type, aspectRatio, result.width, result.height)
  return result
}
